import { useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { RefreshCw, Globe, ListTodo, Search, Shield, Star, ShoppingBag, FileText, Mail, Mic, BookOpen, Clapperboard } from "lucide-react";
import SEOManager from "@/components/admin/SEOManager";
import { TasksCRUD } from "@/components/admin/TasksCRUD";
import { SocialMediaManager } from "@/components/admin/SocialMediaManager";
import CampaignTracker from "@/components/admin/CampaignTracker";
import VideoReviewManager from "@/components/admin/VideoReviewManager";
import { MerchManager } from "@/components/admin/MerchManager";
import { ContentManager } from "@/components/admin/ContentManager";
import { DignityVoiceManager } from "@/components/admin/DignityVoiceManager";
import { DignityNarrationDashboard } from "@/components/admin/DignityNarrationDashboard";
import { BacklinkApprovalManager } from "@/components/admin/BacklinkApprovalManager";
import { ReleaseScreenshotsManager } from "@/components/admin/ReleaseScreenshotsManager";
import { AssetReviewManager } from "@/components/admin/AssetReviewManager";

/* ─── Patriotic Design Tokens ─── */
const T = {
  bg:        "#FAF9F6",
  navy:      "#002868",
  navyDark:  "#001845",
  red:       "#B22234",
  gold:      "#C5A55A",
  goldLight: "#D4B97A",
  text:      "#1B1B1B",
  textMuted: "rgba(27,27,27,0.62)",
  border:    "rgba(27,27,27,0.12)",
  cardBg:    "#FFFFFF",
  shadow:    "0 2px 16px rgba(0,0,0,0.07)",
  shadowMd:  "0 4px 24px rgba(0,0,0,0.10)",
};

const serif = "'Instrument Serif', Georgia, 'Times New Roman', serif";
const sans  = "'Instrument Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif";

/* ─── CSS Variable Override Object ─── */
const themeVars: React.CSSProperties = {
  "--background":             "42 40% 97%",
  "--foreground":             "0 0% 11%",
  "--card":                   "0 0% 100%",
  "--card-foreground":        "0 0% 11%",
  "--popover":                "0 0% 100%",
  "--popover-foreground":     "0 0% 11%",
  "--primary":                "221 100% 32%",
  "--primary-foreground":     "42 40% 97%",
  "--secondary":              "42 20% 93%",
  "--secondary-foreground":   "0 0% 11%",
  "--muted":                  "42 20% 93%",
  "--muted-foreground":       "0 0% 40%",
  "--accent":                 "42 20% 93%",
  "--accent-foreground":      "0 0% 11%",
  "--destructive":            "352 60% 45%",
  "--destructive-foreground": "0 0% 100%",
  "--border":                 "42 10% 85%",
  "--input":                  "42 10% 85%",
  "--ring":                   "221 100% 32%",
  "--radius":                 "0.375rem",
  backgroundColor:            T.bg,
  color:                      T.text,
  fontFamily:                 sans,
  minHeight:                  "100vh",
  display:                    "flex",
  flexDirection:              "column",
} as React.CSSProperties;

/* ─── Tab definitions ─── */
type TabId = "videos" | "merch" | "tasks" | "seo" | "campaigns" | "social" | "content" | "voice" | "narration" | "articles" | "release_shots" | "assets";

const TABS: { id: TabId; label: string; icon: React.ReactNode; desc: string }[] = [
  { id: "videos",  label: "Video Review",   icon: <Clapperboard size={15} />, desc: "Watch and approve classroom cuts — nothing goes live until approved here" },
  { id: "merch",   label: "Merch Store",    icon: <ShoppingBag size={15} />, desc: "Approve products, set prices, manage the store" },
  { id: "articles", label: "Article Reviews", icon: <BookOpen size={15} />, desc: "Approve Claude.ai backlink articles before they appear on scenario pages" },
  { id: "tasks",   label: "Tasks",          icon: <ListTodo    size={15} />, desc: "Manage project tasks" },
  { id: "seo",     label: "SEO",            icon: <Search      size={15} />, desc: "Search & metadata" },
  { id: "campaigns", label: "Campaigns",    icon: <Globe       size={15} />, desc: "Unified marketing tracker — all campaigns + posts + tasks at a glance" },
  { id: "social",  label: "Social Media",   icon: <Globe       size={15} />, desc: "Approve and schedule individual posts" },
  { id: "content", label: "Content",        icon: <FileText    size={15} />, desc: "Blog posts, content calendar, AI generation" },
  { id: "voice",   label: "Dignity Voice",  icon: <Mic         size={15} />, desc: "Narration voice config per language" },
  { id: "narration", label: "Narration Lines", icon: <FileText  size={15} />, desc: "Voice line generation status & audio" },
  { id: "release_shots", label: "Release Screenshots", icon: <FileText size={15} />, desc: "Approve auto-captured screenshots to use as Open Graph share images for release-notes entries" },
  { id: "assets", label: "Asset Review", icon: <Star size={15} />, desc: "Swipe-approve DIGNITY-CIVIC-V1 video assets from the generation queue" },
];

/* ─── Stripes (13 stars-and-stripes style) ─── */
function Stripes({ opacity = 0.55 }: { opacity?: number }) {
  return (
    <div style={{ display: "flex", height: 4 }}>
      {Array.from({ length: 13 }).map((_, i) => (
        <div
          key={i}
          style={{
            flex: 1,
            backgroundColor: i % 2 === 0 ? T.red : "#FAF9F6",
            opacity,
          }}
        />
      ))}
    </div>
  );
}

/* ─── Star Row ─── */
function StarRow({ count = 5, size = 13 }: { count?: number; size?: number }) {
  return (
    <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
      {Array.from({ length: count }).map((_, i) => (
        <Star
          key={i}
          size={size}
          fill={T.gold}
          stroke="none"
          style={{ opacity: 0.85 }}
        />
      ))}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════ */
const Admin = () => {
  const [activeTab, setActiveTab] = useState<TabId>("videos");

  return (
    <div style={themeVars}>
      <Helmet>
        <title>Admin — American Hospital Prices</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      {/* ── Patriotic Header ─────────────────────────────────── */}
      <header
        style={{
          backgroundColor: T.navy,
          boxShadow: "0 3px 24px rgba(0,0,0,0.30)",
          position: "sticky",
          top: 0,
          zIndex: 50,
        }}
      >
        {/* Gold crown stripe */}
        <div style={{ height: 3, background: `linear-gradient(90deg, ${T.gold}, ${T.goldLight}, ${T.gold})` }} />

        {/* Main header row */}
        <div
          style={{
            maxWidth: 1400,
            margin: "0 auto",
            padding: "14px 28px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 16,
          }}
        >
          {/* Brand identity */}
          <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
            <div
              style={{
                width: 44,
                height: 44,
                borderRadius: "50%",
                background: `radial-gradient(circle at 35% 35%, ${T.goldLight}, ${T.gold})`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: `0 0 0 2px rgba(197,165,90,0.35), 0 2px 12px rgba(0,0,0,0.25)`,
                flexShrink: 0,
              }}
            >
              <Shield size={22} color={T.navy} strokeWidth={2.5} />
            </div>
            <div>
              <div
                style={{
                  fontFamily: serif,
                  fontSize: 20,
                  color: "#FAF9F6",
                  fontWeight: 400,
                  letterSpacing: "0.01em",
                  lineHeight: 1.2,
                }}
              >
                Admin Command Center
              </div>
              <div
                style={{
                  fontSize: 10,
                  color: T.gold,
                  letterSpacing: "0.14em",
                  textTransform: "uppercase",
                  fontWeight: 700,
                  marginTop: 1,
                }}
              >
                American Hospital Prices
              </div>
            </div>
          </div>

          {/* Stars – center decoration */}
          <StarRow count={7} size={12} />

          {/* Quick links */}
          <div style={{ display: "flex", gap: 8, flexShrink: 0 }}>
          <Link
            to="/admin/email-templates"
            style={{
              display: "flex",
              alignItems: "center",
              gap: 7,
              backgroundColor: "transparent",
              border: `1px solid rgba(197,165,90,0.38)`,
              borderRadius: 6,
              padding: "7px 15px",
              color: T.gold,
              fontSize: 12,
              fontFamily: sans,
              fontWeight: 600,
              letterSpacing: "0.04em",
              cursor: "pointer",
              transition: "all 0.18s ease",
              textDecoration: "none",
            }}
          >
            <Mail size={13} />
            Email Templates
          </Link>

          {/* Refresh action */}
          <button
            onClick={() => window.location.reload()}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 7,
              backgroundColor: "transparent",
              border: `1px solid rgba(197,165,90,0.38)`,
              borderRadius: 6,
              padding: "7px 15px",
              color: T.gold,
              fontSize: 12,
              fontFamily: sans,
              fontWeight: 600,
              letterSpacing: "0.04em",
              cursor: "pointer",
              transition: "all 0.18s ease",
              flexShrink: 0,
            }}
            onMouseEnter={e => {
              (e.currentTarget as HTMLButtonElement).style.backgroundColor = "rgba(197,165,90,0.12)";
              (e.currentTarget as HTMLButtonElement).style.borderColor = "rgba(197,165,90,0.65)";
            }}
            onMouseLeave={e => {
              (e.currentTarget as HTMLButtonElement).style.backgroundColor = "transparent";
              (e.currentTarget as HTMLButtonElement).style.borderColor = "rgba(197,165,90,0.38)";
            }}
          >
            <RefreshCw size={13} />
            Refresh
          </button>
          </div>
        </div>

        {/* 13-stripe footer on header */}
        <Stripes opacity={0.50} />
      </header>

      {/* ── Tab Navigation ───────────────────────────────────── */}
      <div
        style={{
          backgroundColor: T.bg,
          borderBottom: `1px solid ${T.border}`,
          position: "sticky",
          top: 80,
          zIndex: 40,
        }}
      >
        <div
          style={{
            maxWidth: 1400,
            margin: "0 auto",
            padding: "0 28px",
            display: "flex",
            gap: 0,
          }}
        >
          {TABS.map(tab => {
            const active = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 7,
                  padding: "14px 22px",
                  border: "none",
                  borderBottom: active
                    ? `3px solid ${T.red}`
                    : "3px solid transparent",
                  backgroundColor: active
                    ? "rgba(178,34,52,0.04)"
                    : "transparent",
                  color: active ? T.navy : T.textMuted,
                  fontSize: 13,
                  fontFamily: sans,
                  fontWeight: active ? 700 : 500,
                  letterSpacing: "0.02em",
                  cursor: "pointer",
                  transition: "all 0.18s ease",
                  marginBottom: -1,
                  whiteSpace: "nowrap",
                }}
                onMouseEnter={e => {
                  if (!active) {
                    (e.currentTarget as HTMLButtonElement).style.color = T.navy;
                    (e.currentTarget as HTMLButtonElement).style.backgroundColor = "rgba(0,40,104,0.04)";
                  }
                }}
                onMouseLeave={e => {
                  if (!active) {
                    (e.currentTarget as HTMLButtonElement).style.color = T.textMuted;
                    (e.currentTarget as HTMLButtonElement).style.backgroundColor = "transparent";
                  }
                }}
              >
                <span style={{ opacity: active ? 1 : 0.7 }}>{tab.icon}</span>
                {tab.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* ── Page Content ─────────────────────────────────────── */}
      <main
        style={{
          flex: 1,
          maxWidth: 1400,
          width: "100%",
          margin: "0 auto",
          padding: "32px 28px 64px",
          boxSizing: "border-box",
        }}
      >
        {/* Section header */}
        <div style={{ marginBottom: 24 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 4 }}>
            <div
              style={{
                width: 3,
                height: 22,
                backgroundColor: T.red,
                borderRadius: 2,
                flexShrink: 0,
              }}
            />
            <h2
              style={{
                fontFamily: serif,
                fontSize: 22,
                color: T.navy,
                fontWeight: 400,
                margin: 0,
                letterSpacing: "0.01em",
              }}
            >
              {TABS.find(t => t.id === activeTab)?.label}
            </h2>
          </div>
          <p style={{ margin: 0, fontSize: 13, color: T.textMuted, paddingLeft: 13 }}>
            {TABS.find(t => t.id === activeTab)?.desc}
          </p>
        </div>

        {/* Tab panels */}
        <div style={{ display: activeTab === "videos"  ? "block" : "none" }}><VideoReviewManager /></div>
        <div style={{ display: activeTab === "merch"   ? "block" : "none" }}><MerchManager /></div>
        <div style={{ display: activeTab === "articles" ? "block" : "none" }}><BacklinkApprovalManager /></div>
        <div style={{ display: activeTab === "tasks"   ? "block" : "none" }}><TasksCRUD /></div>
        <div style={{ display: activeTab === "seo"     ? "block" : "none" }}><SEOManager /></div>
        <div style={{ display: activeTab === "campaigns" ? "block" : "none" }}><CampaignTracker /></div>
        <div style={{ display: activeTab === "social"  ? "block" : "none" }}><SocialMediaManager /></div>
        <div style={{ display: activeTab === "content" ? "block" : "none" }}><ContentManager /></div>
        <div style={{ display: activeTab === "voice"   ? "block" : "none" }}><DignityVoiceManager /></div>
        <div style={{ display: activeTab === "narration" ? "block" : "none" }}><DignityNarrationDashboard /></div>
        <div style={{ display: activeTab === "release_shots" ? "block" : "none" }}><ReleaseScreenshotsManager /></div>
        <div style={{ display: activeTab === "assets" ? "block" : "none" }}><AssetReviewManager /></div>
      </main>

      {/* ── Patriotic Footer Bar ─────────────────────────────── */}
      <footer style={{ backgroundColor: T.navy }}>
        <Stripes opacity={0.4} />
        <div
          style={{
            maxWidth: 1400,
            margin: "0 auto",
            padding: "10px 28px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <span style={{ fontSize: 11, color: "rgba(197,165,90,0.6)", letterSpacing: "0.08em", textTransform: "uppercase" }}>
            American Hospital Prices — Admin
          </span>
          <StarRow count={3} size={9} />
        </div>
      </footer>
    </div>
  );
};

export default Admin;
